import { AnimatePresence, motion } from "framer-motion";
import { useTheme } from "../providers/ThemeProvider";

type PillOption<T> = {
  label: string;
  value: T;
  color: string; // 'green', 'red', etc.
};

type PillSwitchProps<T> = {
  options: PillOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
};

const TailwindColorHexMap: Record<string, { light: string; dark: string }> = {
  red:    { light: "#ef4444", dark: "#f87171" },
  green:  { light: "#059669", dark: "#34d399" },
  blue:   { light: "#3b82f6", dark: "#60a5fa" },
  yellow: { light: "#eab308", dark: "#facc15" },
  gray:   { light: "#9ca3af", dark: "#6b7280" },
  orange: { light: "#f97316", dark: "#fb923c" },
  purple: { light: "#8b5cf6", dark: "#a78bfa" },
  pink:   { light: "#ec4899", dark: "#f472b6" },
  teal:   { light: "#14b8a6", dark: "#2dd4bf" },
};

const PillSwitch = <T,>({
  options,
  disabled,
  value,
  onChange,
}: PillSwitchProps<T>) => {
  const { isDark } = useTheme();

  const activeIdx = options.findIndex((opt) => opt.value === value);
  const toggle = () => {
    const nextIdx = activeIdx === 0 ? 1 : 0;
    onChange(options[nextIdx].value);
  };

  const getColor = (colorName: string) => {
    const entry = TailwindColorHexMap[colorName];
    if (!entry) return isDark ? "#34d399" : "#059669";
    return isDark ? entry.dark : entry.light;
  };

  return (
    <div
      className="flex items-center justify-center"
      style={{ minWidth: 160, opacity: disabled ? 0.5 : 1 }}
    >
      <div
        className="relative flex w-40 h-7 rounded-full transition-colors duration-200 cursor-pointer select-none bg-gray-100 dark:bg-neutral-700"
        onClick={disabled ? undefined : toggle}
      >
        {/* Animated active pill */}
        <AnimatePresence initial={false}>
          {options.map((option, idx) => {
            if (value === option.value) {
              const color = getColor(option.color);
              return (
                <motion.div
                  key={(option.value as string) || idx.toString()}
                  layoutId="active-pill"
                  className="absolute z-0 rounded-full"
                  initial={false}
                  animate={{
                    left: `calc(${(100 / options.length) * idx}%)`,
                    width: `calc(100% / ${options.length})`,
                    height: "100%",
                    top: 0,
                    backgroundColor: color,
                  }}
                  exit={{}}
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              );
            }
            return null;
          })}
        </AnimatePresence>
        {/* Buttons */}
        {options.map((option, index) => {
          const isActive = value === option.value;
          const color = getColor(option.color);
          return (
            <button
              key={(option.value as string) || index}
              className={`flex-1 flex items-center justify-center rounded-full transition-all duration-200 font-semibold text-sm relative z-10 ${
                isActive
                  ? "shadow-sm text-white"
                  : "text-gray-500 dark:text-neutral-400 hover:bg-black/5 dark:hover:bg-white/10"
              } ${disabled ? "cursor-not-allowed" : ""}`}
              style={{
                backgroundColor: "transparent",
                boxShadow: isActive ? `0 2px 8px 0 ${color}44` : undefined,
                transition: "all 0.2s cubic-bezier(.4,0,.2,1)",
                outline: "none",
              }}
              type="button"
              tabIndex={-1}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PillSwitch;
